/**
 * 合并一次模型 Undo/Redo 事务内的设计树完整刷新请求。
 *
 * 该对象只管理 UI 派生刷新边界，不读取或修改 CAD 主状态。事务外请求保持立即执行；
 * 事务内请求只登记一次，并在最外层状态恢复全部结束后统一刷新。
 */
export class FeatureTreeHistoryRefreshCoordinator {
  /** 当前嵌套历史恢复深度。 */
  private transactionDepth = 0;

  /** 当前历史事务是否请求过完整刷新。 */
  private refreshPending = false;

  /** 协调器是否已经释放。 */
  private disposed = false;

  /**
   * 创建历史设计树刷新协调器。
   *
   * @param refresh 执行一次权威设计树刷新的异步入口。
   * @param reportFailure 刷新失败报告入口；刷新失败不覆盖原历史事务结果。
   */
  public constructor(
    private readonly refresh: () => Promise<void>,
    private readonly reportFailure: (error: unknown) => void,
  ) {}

  /**
   * 请求刷新设计树。
   *
   * @returns 事务外等待实际刷新；事务内只等待请求登记完成。
   */
  public request(): Promise<void> {
    if (this.disposed) {
      return Promise.resolve();
    }
    if (this.transactionDepth > 0) {
      this.refreshPending = true;
      return Promise.resolve();
    }
    return this.executeRefresh();
  }

  /**
   * 在一个可嵌套的历史恢复事务中执行状态写回。
   *
   * 最外层事务无论成功或失败都会结算已经登记的刷新；刷新异常只报告，不替换调用方
   * 原始返回值或异常。释放后的协调器不再管理刷新，但仍允许调用方完成自身清理。
   *
   * @param operation 历史主状态与派生状态恢复操作。
   * @returns 原操作结果。
   */
  public async run<T>(operation: () => Promise<T>): Promise<T> {
    if (this.disposed) {
      return operation();
    }

    this.transactionDepth += 1;
    try {
      return await operation();
    } finally {
      this.transactionDepth -= 1;
      if (this.transactionDepth === 0 && this.refreshPending) {
        this.refreshPending = false;
        await this.executeRefresh();
      }
    }
  }

  /**
   * 释放协调器并丢弃尚未结算的 UI 刷新请求。
   */
  public dispose(): void {
    this.disposed = true;
    this.transactionDepth = 0;
    this.refreshPending = false;
  }

  /**
   * 执行一次刷新并集中报告异常。
   */
  private async executeRefresh(): Promise<void> {
    if (this.disposed) {
      return;
    }
    try {
      await this.refresh();
    } catch (error) {
      this.reportFailure(error);
    }
  }
}
